import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { useRotationsplanById } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanById/useRotationsplanById';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { UPDATE_FORM_ID } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/constants';
import { StandortSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/components/StandortSelect';
import { Suspense } from 'react';
import { ArbeitsbereichSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/components/ArbeitsbereichSelect';
import { hasUeberschneidungen } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/utils/hasUeberschneidungen';
import {
  Button,
  FieldError,
  Label,
  SheetClose,
  SheetHeader,
  SheetTitle,
  Skeleton,
  Switch,
  Textarea,
} from '@e2n/ui';
import { DeleteArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/features/DeleteArbeitsbereichStation/DeleteArbeitsbereichStation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { AbteilungsleiterSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/AbteilungsleiterSelect/AbteilungsleiterSelect';
import { FieldBase } from '@/components/form';
import { useForm } from '@/domain/mitarbeiter/features/rotationsplan/features/UpdateStation/useForm';
import { useRegisterSheetForm } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/SheetWrapper/useRegisterSheetForm';
import { DatePickerInputGroup } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/components/DatepickerInputGroup';
import { ErinnerungsZeitenMultiSelect } from '@/domain/mitarbeiter/shared/components/ErinnerungsZeitenMultiSelect/ErinnerungsZeitenMultiSelect';
import { AbteilungsleiterMultiSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/AbteilungsleiterMultiSelect/AbteilungsleiterMultiSelect';
import dayjs from 'dayjs';

export function UpdateStation() {
  const { t } = useTranslation();
  const { id, abschnittId, stationId } = useParams();
  const { currentStandort } = useStandortContext();
  const { rotationsplan } = useRotationsplanById(id);
  const navigate = useNavigate();

  const station = rotationsplan.allStationen
    .filter((station) => station instanceof ArbeitsbereichStation)
    .find((station) => station.id === stationId);

  if (!rotationsplan) {
    navigate('/mitarbeiter/rotationsplaene');
  } else if (!station) {
    navigate(`/mitarbeiter/rotationsplan/${rotationsplan.id}`);
  }

  const form = useForm({ abschnittId, rotationsplan, station });

  useRegisterSheetForm(form);

  return (
    <>
      <SheetHeader className="space-y-0 flex flex-row items-center justify-between bg-neutrals-200 px-4 py-2">
        <div className="flex items-center gap-2">
          <SheetClose>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('actions.back')}
              className="cursor-pointer">
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
          </SheetClose>
          <SheetTitle className="text-sm text-neutrals-800">
            {t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.updateStation.title',
            )}
          </SheetTitle>
        </div>
        <div className="flex gap-3 items-center md:hidden">
          <DeleteArbeitsbereichStation station={station} />
          <Button form={UPDATE_FORM_ID} variant="ghost-primary">
            {t('actions.fertig')}
          </Button>
        </div>
      </SheetHeader>
      <div className="flex flex-col gap-4 px-6 grow overflow-auto">
        <div className="text-xs text-neutrals-700">
          {t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.hinweisPflichtfelder',
          )}
        </div>
        <form
          id={UPDATE_FORM_ID}
          className="flex flex-col gap-4 h-full"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}>
          <form.AppField
            name="standort"
            listeners={{
              onChange: () => form.setFieldValue('arbeitsbereich', undefined),
            }}>
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <FieldBase
                  isRequired
                  label={t(
                    'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.standort.label',
                  )}
                  data-invalid={isInvalid}
                  className="flex flex-col gap-1 w-full">
                  <StandortSelect
                    handleChange={field.handleChange}
                    value={field.state.value}
                    land={rotationsplan.land}
                  />
                </FieldBase>
              );
            }}
          </form.AppField>
          <form.AppField
            name="arbeitsbereich"
            listeners={{
              onChange: () => form.setFieldValue('abteilungsleiter', undefined),
            }}>
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <FieldBase
                  isRequired
                  data-invalid={isInvalid}
                  className="flex flex-col gap-1 w-full"
                  label={t(
                    'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.arbeitsbereich.label',
                  )}>
                  <Suspense fallback={<Skeleton className="w-108 h-10" />}>
                    <ArbeitsbereichSelect
                      value={field.state.value}
                      standortId={
                        form.getFieldValue('standort.id') ?? currentStandort.id
                      }
                      handleChange={field.handleChange}
                      mitarbeiter={rotationsplan.mitarbeiter}
                    />
                  </Suspense>
                </FieldBase>
              );
            }}
          </form.AppField>
          <div className="flex flex-col md:flex-row gap-4">
            <form.AppField
              name="von"
              validators={{
                onChange: ({ value, fieldApi }) => {
                  const bis = fieldApi.form.getFieldValue('bis');

                  if (dayjs(value).isAfter(bis)) {
                    return {
                      message: t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.endeVorBeginn',
                      ),
                    };
                  }
                },
              }}
              listeners={{
                onChange: () => {
                  form.setFieldValue('erinnerungsZeit', []);
                },
              }}>
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <FieldBase
                    isRequired
                    data-invalid={isInvalid}
                    className="flex flex-col gap-1 w-full"
                    label={t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.von.label',
                    )}>
                    <DatePickerInputGroup
                      defaultDate={field.state.value}
                      onSelect={field.handleChange}
                      label={t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.von.label',
                      )}
                    />
                  </FieldBase>
                );
              }}
            </form.AppField>
            <form.AppField
              name="bis"
              listeners={{
                onChange: () => {
                  form.validateField('von', 'change');
                },
              }}>
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <FieldBase
                    isRequired
                    data-invalid={isInvalid}
                    className="flex flex-col gap-1 w-full"
                    label={t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.bis.label',
                    )}>
                    <DatePickerInputGroup
                      defaultDate={field.state.value}
                      onSelect={field.handleChange}
                      label={t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.bis.label',
                      )}
                    />
                  </FieldBase>
                );
              }}
            </form.AppField>
          </div>
          <form.Subscribe
            selector={(state) => ({
              von: state.values.von,
              bis: state.values.bis,
            })}>
            {({ von, bis }) =>
              von <= bis &&
              hasUeberschneidungen(
                rotationsplan.allStationen,
                station,
                von,
                bis,
              ) ? (
                <FieldError
                  variant="warning"
                  errors={[
                    {
                      message: t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.hasUeberschneidung',
                      ),
                    },
                  ]}
                />
              ) : null
            }
          </form.Subscribe>
          <form.AppField name="lernziele">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <FieldBase
                  data-invalid={isInvalid}
                  className="flex flex-col gap-1 w-full"
                  label={t(
                    'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.lernziele.label',
                  )}>
                  <Textarea
                    defaultValue={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </FieldBase>
              );
            }}
          </form.AppField>
          <form.AppField
            name="abteilungsleiter"
            listeners={{
              onChange: ({ value }) => {
                form.setFieldValue('erinnerungAn', value?.id ? [value.id] : []);
              },
            }}>
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <FieldBase
                  label={t(
                    'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.label',
                  )}
                  infoText={t(
                    'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.tooltip',
                  )}
                  data-invalid={isInvalid}
                  className="flex flex-col gap-1 w-full">
                  <Suspense fallback={<Skeleton className="w-108 h-10" />}>
                    <form.Subscribe
                      selector={(state) => state.values.arbeitsbereich?.id}>
                      {(selectedArbeitsbereichId) => (
                        <AbteilungsleiterSelect
                          selectedArbeitsbereichId={selectedArbeitsbereichId}
                          onChange={field.handleChange}
                          selectedAbteilungsleiter={field.state.value}
                        />
                      )}
                    </form.Subscribe>
                  </Suspense>
                </FieldBase>
              );
            }}
          </form.AppField>
          <form.Subscribe selector={(state) => state.values.abteilungsleiter}>
            {(abteilungsleiter) => (
              <form.AppField
                listeners={{
                  onChange: ({ value }) => {
                    if (!value) {
                      form.setFieldValue('erinnerungsZeit', []);
                      form.setFieldValue(
                        'erinnerungAn',
                        abteilungsleiter?.id ? [abteilungsleiter.id] : [],
                      );
                    }
                  },
                }}
                name="fristErinnerung">
                {(field) => (
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={field.name}
                        size="sm"
                        checked={field.state.value}
                        onCheckedChange={(value) => {
                          field.handleChange(value);
                        }}
                      />

                      <Label htmlFor={field.name}>
                        {t(
                          'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.fristErinnerung',
                        )}
                      </Label>
                    </div>
                    {field.state.value && station.frist && (
                      <div className="text-xs text-neutrals-800">
                        {t(
                          'domain.mitarbeiter.rotationsplan.rotationsplaene.updateStation.fristHinweis',
                        )}
                      </div>
                    )}
                  </div>
                )}
              </form.AppField>
            )}
          </form.Subscribe>
          <form.Subscribe
            selector={(state) => ({
              fristErinnerung: state.values.fristErinnerung,
              von: state.values.von,
              arbeitsbereichId: state.values.arbeitsbereich?.id,
              abteilungsleiterId: state.values.abteilungsleiter?.id,
            })}>
            {({ fristErinnerung, von, arbeitsbereichId }) =>
              fristErinnerung && (
                <>
                  <form.AppField name="erinnerungsZeit">
                    {(field) => (
                      <FieldBase
                        label={t(
                          'domain.mitarbeiter.fristen.erinnerungsZeit.label',
                        )}
                        isRequired>
                        <ErinnerungsZeitenMultiSelect
                          values={field.state.value}
                          handleChange={(value) => field.handleChange(value)}
                          fristdatum={von?.toISOString()}
                        />
                      </FieldBase>
                    )}
                  </form.AppField>
                  <form.AppField name="erinnerungAn">
                    {(field) => (
                      <FieldBase
                        label={t(
                          'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.erinnerungAn.label',
                        )}
                        isRequired>
                        <Suspense
                          fallback={<Skeleton className="w-108 h-10" />}>
                          <AbteilungsleiterMultiSelect
                            values={field.state.value}
                            handleChange={(value) => field.handleChange(value)}
                            arbeitsbereichId={arbeitsbereichId}
                          />
                        </Suspense>
                      </FieldBase>
                    )}
                  </form.AppField>
                </>
              )
            }
          </form.Subscribe>
        </form>
      </div>
      <div className="h-px w-full bg-neutrals-300" />
      <div className="hidden md:flex justify-end gap-4 px-8 pb-4">
        <DeleteArbeitsbereichStation station={station} />
        <Button
          disabled={!form.state.isValid || form.state.isSubmitting}
          form={UPDATE_FORM_ID}>
          {t('actions.fertig')}
        </Button>
      </div>
    </>
  );
}
